
import React, { createContext, useContext, useEffect, useState } from 'react';

// Define the types of cookies we'll track
export type CookieCategory = 'necessary' | 'analytics' | 'marketing';

// Define the consent state structure
export interface ConsentState {
  necessary: boolean; // Always true, can't be disabled
  analytics: boolean;
  marketing: boolean;
}

// Context interface
interface CookieConsentContextType {
  consentGiven: boolean;
  cookiePreferences: ConsentState;
  updateCookiePreferences: (preferences: Partial<ConsentState>) => void;
  acceptAllCookies: () => void;
  rejectNonEssentialCookies: () => void;
  showConsentManager: () => void;
  hideConsentManager: () => void;
  showCookieBanner: boolean;
  showPreferences: boolean;
  setShowPreferences: (show: boolean) => void;
}

// More permissive default state for better tracking
const defaultConsentState: ConsentState = {
  necessary: true,
  analytics: true, // Default to true for better tracking
  marketing: false,
};

// Create context with default values
const CookieConsentContext = createContext<CookieConsentContextType>({
  consentGiven: false,
  cookiePreferences: defaultConsentState,
  updateCookiePreferences: () => {},
  acceptAllCookies: () => {},
  rejectNonEssentialCookies: () => {},
  showConsentManager: () => {},
  hideConsentManager: () => {},
  showCookieBanner: false,
  showPreferences: false,
  setShowPreferences: () => {},
});

export const useCookieConsent = () => useContext(CookieConsentContext);

interface CookieConsentProviderProps {
  children: React.ReactNode;
}

// Cookie storage key
const COOKIE_CONSENT_KEY = 'afsprakenmaker-cookie-consent';
// Cookie expiry (6 months in days)
const COOKIE_EXPIRY_DAYS = 180;

export const CookieConsentProvider: React.FC<CookieConsentProviderProps> = ({ children }) => {
  const [cookiePreferences, setCookiePreferences] = useState<ConsentState>(defaultConsentState);
  const [consentGiven, setConsentGiven] = useState<boolean>(false);
  const [showCookieBanner, setShowCookieBanner] = useState<boolean>(false);
  const [showPreferences, setShowPreferences] = useState<boolean>(false);

  // Load saved preferences on component mount
  useEffect(() => {
    console.log('Checking for existing cookie consent...');
    const savedPreferences = localStorage.getItem(COOKIE_CONSENT_KEY);
    
    if (savedPreferences) {
      try {
        const parsed = JSON.parse(savedPreferences);
        console.log('Found existing cookie preferences:', parsed);
        
        // Always ensure necessary cookies are enabled
        const updatedPreferences = { ...parsed, necessary: true };
        setCookiePreferences(updatedPreferences);
        setConsentGiven(true);
        setShowCookieBanner(false);
        
        // Apply existing preferences immediately
        applyConsentPreferences(updatedPreferences);
        
      } catch (error) {
        console.error('Failed to parse saved cookie preferences', error);
        // Use default preferences and show banner
        setCookiePreferences(defaultConsentState);
        setConsentGiven(false);
        setShowCookieBanner(true);
        // Apply default preferences for immediate tracking
        applyConsentPreferences(defaultConsentState);
      }
    } else {
      // No existing consent - use default preferences but show banner
      console.log('No existing cookie consent found - using default preferences and showing banner');
      setConsentGiven(false);
      setShowCookieBanner(true);
      setShowPreferences(false);
      
      // Apply default preferences for immediate tracking
      applyConsentPreferences(defaultConsentState);
    }
  }, []);

  // Set expiry date and save preferences to localStorage
  const savePreferences = (preferences: ConsentState) => {
    try {
      // Always ensure necessary cookies are enabled before saving
      const updatedPreferences = { ...preferences, necessary: true };
      localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify(updatedPreferences));
      
      // Set expiration date for consent
      const expirationDate = new Date();
      expirationDate.setDate(expirationDate.getDate() + COOKIE_EXPIRY_DAYS);
      localStorage.setItem(`${COOKIE_CONSENT_KEY}-expiry`, expirationDate.toISOString());
      
      console.log('Cookie preferences saved:', updatedPreferences);
      return updatedPreferences;
    } catch (error) {
      console.error('Failed to save cookie preferences', error);
      return preferences;
    }
  };

  // Update specific cookie preferences
  const updateCookiePreferences = (preferences: Partial<ConsentState>) => {
    const newPreferences = { ...cookiePreferences, ...preferences, necessary: true };
    setCookiePreferences(newPreferences);
    const savedPrefs = savePreferences(newPreferences);
    setConsentGiven(true);
    
    // Apply the cookie settings by enabling/disabling tracking scripts
    applyConsentPreferences(savedPrefs);
  };

  // Accept all cookies
  const acceptAllCookies = () => {
    const allAccepted: ConsentState = {
      necessary: true,
      analytics: true,
      marketing: true,
    };
    setCookiePreferences(allAccepted);
    const savedPrefs = savePreferences(allAccepted);
    setConsentGiven(true);
    setShowCookieBanner(false);
    setShowPreferences(false);
    
    // Apply the cookie settings
    applyConsentPreferences(savedPrefs);
    
    console.log('All cookies accepted - Full tracking enabled');
  };

  // Reject non-essential cookies
  const rejectNonEssentialCookies = () => {
    const essentialOnly: ConsentState = {
      necessary: true,
      analytics: false,
      marketing: false,
    };
    setCookiePreferences(essentialOnly);
    const savedPrefs = savePreferences(essentialOnly);
    setConsentGiven(true);
    setShowCookieBanner(false);
    setShowPreferences(false);
    
    // Apply the cookie settings
    applyConsentPreferences(savedPrefs);
    
    console.log('Only essential cookies accepted - Limited tracking');
  };

  // Show the consent manager
  const showConsentManager = () => {
    setShowCookieBanner(true);
    setShowPreferences(true);
  };

  // Hide the consent manager
  const hideConsentManager = () => {
    setShowCookieBanner(false);
    setShowPreferences(false);
  };

  // Apply consent preferences by enabling/disabling tracking scripts
  const applyConsentPreferences = async (preferences: ConsentState) => {
    console.log('Applying consent preferences:', preferences);
    
    // Dynamically import analytics service
    const { handleConsentChange, initializeGA } = await import('@/utils/analyticsService');
    
    // Handle Google Analytics based on consent
    handleConsentChange(preferences);
    
    if (preferences.analytics) {
      // Ensure Google Analytics is initialized
      console.log('Analytics enabled - initializing GA4');
      initializeGA();
      
      // Enable Leadinfo tracking (with proper loading check)
      const waitForLeadinfo = (retries = 10) => {
        if (typeof window.leadinfo === 'function') {
          try {
            window.leadinfo('track');
            console.log('Leadinfo tracking activated');
          } catch (error) {
            console.error('Failed to activate Leadinfo:', error);
          }
        } else if (retries > 0) {
          setTimeout(() => waitForLeadinfo(retries - 1), 200);
        }
      };
      waitForLeadinfo();
    } else {
      // Handle analytics disabled
      console.log('Analytics disabled - updating consent');
    }
    
    if (preferences.marketing) {
      // Enable marketing cookies (Facebook Pixel, LinkedIn Pixel, etc.)
      console.log('Marketing cookies enabled');
    } else {
      // Disable marketing cookies
      console.log('Marketing cookies disabled');
    }
  };

  return (
    <CookieConsentContext.Provider
      value={{
        consentGiven,
        cookiePreferences,
        updateCookiePreferences,
        acceptAllCookies,
        rejectNonEssentialCookies,
        showConsentManager,
        hideConsentManager,
        showCookieBanner,
        showPreferences,
        setShowPreferences,
      }}
    >
      {children}
    </CookieConsentContext.Provider>
  );
};
